import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Star, Heart, Sun, Calendar, Sparkles, Hash,
  Flower2, MessageCircle, HelpCircle, BookOpen, ArrowRight,
} from 'lucide-react';
import { useLang } from '../context/LanguageContext';

/**
 * RelatedTools — single source of truth for internal cross-linking
 * between every tool page on viratastro.cloud.
 *
 * Drop this just above <Footer /> on any page. It auto-hides the tile
 * matching the current route so the user never sees a link to the page
 * they are already on.
 *
 * Why this exists:
 *  - SEO: distributes PageRank across all 10 high-value pages
 *  - UX: keeps users discovering tools without bouncing
 *  - DRY: one component, one place to update when a new tool ships
 */

const TOOLS = [
  {
    path: '/kundli',
    icon: Star,
    iconColor: 'text-amber-400',
    titleHi: 'नि:शुल्क कुंडली',
    titleEn: 'Free Kundli',
    descHi: 'Swiss Ephemeris + पराशरीय फलादेश',
    descEn: 'Swiss Ephemeris + Parashari predictions',
  },
  {
    path: '/gun-milan',
    icon: Heart,
    iconColor: 'text-pink-400',
    titleHi: 'गुण मिलान',
    titleEn: 'Gun Milan',
    descHi: '36 गुण + मांगलिक विश्लेषण',
    descEn: '36 Gunas + Manglik analysis',
  },
  {
    path: '/daily-rashifal',
    icon: Sun,
    iconColor: 'text-yellow-400',
    titleHi: 'आज का राशिफल',
    titleEn: 'Daily Rashifal',
    descHi: '12 राशियाँ + आज का आकाश दर्शन',
    descEn: '12 Rashis + today\'s planetary transit',
  },
  {
    path: '/daily-panchang',
    icon: Calendar,
    iconColor: 'text-blue-400',
    titleHi: 'दैनिक पञ्चांग',
    titleEn: 'Daily Panchang',
    descHi: 'तिथि, नक्षत्र, राहु काल, मुहूर्त',
    descEn: 'Tithi, Nakshatra, Rahu Kaal, Muhurat',
  },
  {
    path: '/daily-tarot',
    icon: Sparkles,
    iconColor: 'text-purple-400',
    titleHi: 'आज का टैरो',
    titleEn: 'Daily Tarot',
    descHi: '3-card spread + combined reading',
    descEn: '3-card spread + combined reading',
  },
  {
    path: '/numerology',
    icon: Hash,
    iconColor: 'text-emerald-400',
    titleHi: 'अंक ज्योतिष',
    titleEn: 'Numerology',
    descHi: 'मूलांक + भाग्यांक विश्लेषण',
    descEn: 'Mulank + Bhagyank analysis',
  },
  {
    path: '/upay',
    icon: Flower2,
    iconColor: 'text-orange-400',
    titleHi: 'नवग्रह उपाय',
    titleEn: 'Navagraha Upay',
    descHi: 'मन्त्र, रुद्राक्ष, रत्न — 9 ग्रह',
    descEn: 'Mantras, Rudraksha, Ratna — 9 planets',
  },
  {
    path: '/poocho-guruji',
    icon: MessageCircle,
    iconColor: 'text-gold',
    titleHi: 'पूछो Guruji',
    titleEn: 'Pucho Guruji',
    descHi: 'AI पराशरीय उत्तर • मात्र ₹99',
    descEn: 'AI Parashari answer • Just ₹99',
    badge: '₹99',
  },
  {
    path: '/faq',
    icon: HelpCircle,
    iconColor: 'text-cyan-400',
    titleHi: 'ज्योतिष FAQs',
    titleEn: 'Astrology FAQs',
    descHi: '47+ शास्त्र-सम्मत प्रश्नोत्तर',
    descEn: '47+ shastra-based Q&A',
  },
  {
    path: '/blog',
    icon: BookOpen,
    iconColor: 'text-rose-400',
    titleHi: 'ज्योतिष ब्लॉग',
    titleEn: 'Astrology Blog',
    descHi: 'पराशरीय लेख + remedies',
    descEn: 'Parashari articles + remedies',
  },
];

export default function RelatedTools({ title, subtitle, exclude = [] }) {
  const { lang } = useLang();
  const { pathname } = useLocation();

  // Hide the tile that matches the current page (and any extra excludes).
  // For nested routes like /upay/surya we still want to show /upay tile,
  // so we only match on exact path or top-level path prefix.
  const normalised = pathname.replace(/\/+$/, '') || '/';
  const excludeSet = new Set([normalised, ...exclude]);

  const tiles = TOOLS.filter((t) => !excludeSet.has(t.path));

  const heading = title || (lang === 'hi' ? 'अन्य ज्योतिष उपकरण' : 'Other Astrology Tools');
  const sub =
    subtitle ||
    (lang === 'hi'
      ? 'आचार्य Vivek जी द्वारा निर्मित — सभी निःशुल्क, सटीक, और पराशरीय शास्त्र पर आधारित।'
      : 'Built by Acharya Vivek — all free, accurate, and rooted in Parashari Jyotish.');

  return (
    <section className="section-padding bg-deep-violet/20 border-t border-mystic-gold/10" data-testid="related-tools">
      <div className="container-custom max-w-6xl">
        <div className="text-center mb-8">
          <p className="text-[11px] tracking-[0.3em] uppercase text-mystic-gold font-semibold mb-2">
            {lang === 'hi' ? 'और भी देखें' : 'Explore More'}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl text-cream-soft mb-2">{heading}</h2>
          <p className="text-cream-soft/70 text-sm sm:text-base max-w-2xl mx-auto">{sub}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {tiles.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.path}
                to={t.path}
                data-testid={`related-link-${t.path.replace(/\//g, '-')}`}
                className="group relative glass-card p-4 sm:p-5 border-mystic-gold/15 hover:border-mystic-gold/45 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-mystic-gold/10"
              >
                {t.badge && (
                  <span className="absolute top-2.5 right-2.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-mystic-gold text-deep-violet">
                    {t.badge}
                  </span>
                )}
                <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-mystic-gold/10 flex items-center justify-center mb-3 group-hover:bg-mystic-gold/20 transition-colors`}>
                  <Icon className={`w-5 h-5 ${t.iconColor}`} />
                </div>
                <h3 className="text-cream-soft font-heading text-base sm:text-lg leading-tight mb-1">
                  {lang === 'hi' ? t.titleHi : t.titleEn}
                </h3>
                <p className="text-cream-soft/60 text-xs leading-relaxed mb-2 line-clamp-2">
                  {lang === 'hi' ? t.descHi : t.descEn}
                </p>
                <span className="inline-flex items-center gap-1 text-mystic-gold text-xs font-semibold opacity-80 group-hover:opacity-100 group-hover:gap-1.5 transition-all">
                  {lang === 'hi' ? 'खोलें' : 'Open'} <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
